(() => {
  const configId = 'cart-reminder-popup-config';
  const modalContentSelector = '#cart-reminder-modal';
  const lastVisitKey = 'krown-cart-reminder-last-visit';
  const sessionGuardKey = 'krown-cart-reminder-attempted';

  const configElm = document.getElementById(configId);
  if (!configElm) return;

  const enabled = configElm.dataset.enabled === 'true';
  const designMode = configElm.dataset.designMode === 'true';
  const hours = Number(configElm.dataset.hours || '0');
  const delaySeconds = Number(configElm.dataset.delay || '0');
  const showRewards = configElm.dataset.showRewards === 'true';
  const dynamicTitle = configElm.dataset.dynamicTitle === 'true';
  const maxItems = Math.max(1, Number(configElm.dataset.maxItems || '3'));

  if (!enabled) return;
  if (designMode) return;
  if (sessionStorage.getItem(sessionGuardKey) === 'true') return;

  const path = window.location.pathname;
  if (path.startsWith('/cart') || path.startsWith('/checkout')) return;

  const now = Date.now();
  const lastVisitRaw = localStorage.getItem(lastVisitKey);
  const lastVisit = lastVisitRaw ? Number(lastVisitRaw) : null;

  localStorage.setItem(lastVisitKey, String(now));

  if (!lastVisit || Number.isNaN(lastVisit)) return;

  const thresholdMs = Math.max(0, hours) * 3600000;
  if (now - lastVisit < thresholdMs) return;

  const labels = {
    titleStatic: configElm.dataset.title || '',
    titleOne: configElm.dataset.titleOne || 'Your selection is waiting for you',
    titleOther: configElm.dataset.titleOther || 'Your {{ count }} items are waiting for you',
    body: configElm.dataset.body || '',
    defaultBody: configElm.dataset.defaultBody || '',
    checkout: configElm.dataset.checkoutLabel || 'Complete order',
    viewCart: configElm.dataset.viewCartLabel || 'View cart',
    itemsOne: configElm.dataset.localeItemsOne || '1 product',
    itemsOther: configElm.dataset.localeItemsOther || '{{ count }} products',
    meta: configElm.dataset.localeMeta || '{{ count_label }} · {{ total }}',
    moreItems: configElm.dataset.localeMoreItems || '+ {{ count }} more',
    quantity: configElm.dataset.localeQuantity || 'Qty {{ count }}',
    cartUrl: configElm.dataset.cartUrl || '/cart',
  };

  const rewardsEnabled = configElm.dataset.rewardsEnabled === 'true';

  const formatMoney = (cents) => {
    const format = window.KROWN?.settings?.shop_money_format;
    const isoCode = window.KROWN?.settings?.iso_code || '';

    if (!format || typeof cents !== 'number') {
      return `${(cents / 100).toFixed(2)}${isoCode}`;
    }

    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return `${(cents / 100).toFixed(2)}${isoCode}`;

    const withDelimiters = (n, prec, thousands, decimal) => {
      thousands = thousands || ',';
      decimal = decimal || '.';
      const num = (n / 100).toFixed(prec);
      const parts = num.split('.');
      const intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return intPart + (parts[1] ? decimal + parts[1] : '');
    };

    let value;
    switch (match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'");
        break;
      default:
        value = (cents / 100).toFixed(2);
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value) + isoCode;
  };

  const replaceCount = (template, count) => template.replace(/\{\{\s*count\s*\}\}/g, String(count));

  const getTitle = (itemCount) => {
    if (dynamicTitle) {
      return itemCount === 1
        ? labels.titleOne
        : replaceCount(labels.titleOther, itemCount);
    }
    return labels.titleStatic;
  };

  const getMetaText = (itemCount, subtotalCents) => {
    const countLabel = itemCount === 1
      ? labels.itemsOne
      : replaceCount(labels.itemsOther, itemCount);
    const total = formatMoney(subtotalCents);
    return labels.meta
      .replace(/\{\{\s*count_label\s*\}\}/g, countLabel)
      .replace(/\{\{\s*total\s*\}\}/g, `<strong>${total}</strong>`);
  };

  const getBodyMarkup = () => {
    const bodyText = labels.body || labels.defaultBody;
    if (!bodyText) return '';
    return `<div class="rte text-align--left cart-reminder-popup__body"><p>${escapeHtml(bodyText)}</p></div>`;
  };

  const imageUrl = (url, width) => {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const escapeHtml = (value) => {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  const getRewardsMarkup = () => {
    if (!showRewards || !rewardsEnabled) return '';

    const sidebarRewards = document.querySelector('#site-cart-sidebar cart-rewards-progress');
    if (sidebarRewards) {
      return `<div class="cart-reminder-popup__rewards">${sidebarRewards.outerHTML}</div>`;
    }

    const rewardsSource = document.getElementById('cart-reminder-rewards-source');
    if (rewardsSource?.innerHTML.trim()) {
      return `<div class="cart-reminder-popup__rewards">${rewardsSource.innerHTML}</div>`;
    }

    return '';
  };

  const renderItem = (item) => {
    const title = escapeHtml(item.product_title || item.title || '');
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
      ? escapeHtml(item.variant_title)
      : '';
    const quantityLine = item.quantity > 1
      ? replaceCount(labels.quantity, item.quantity)
      : '';
    const variantMarkup = variantTitle || quantityLine
      ? `<span class="cart-item__variant text-size--small text-color--opacity">${variantTitle}${variantTitle && quantityLine ? ' · ' : ''}${quantityLine}</span>`
      : '';

    return `
      <div class="cart-item" data-product-id="${item.product_id}">
        <a href="${escapeHtml(item.url)}" class="cart-item__thumbnail element--border-width-clamped element--border-radius">
          <img
            src="${imageUrl(item.image, 120)}"
            alt=""
            width="120"
            height="120"
            loading="lazy"
          >
        </a>
        <div class="cart-item__content">
          <div>
            <div class="cart-item__price">
              <strong>${formatMoney(item.final_line_price)}</strong>
            </div>
            <a href="${escapeHtml(item.url)}" class="cart-item__title">
              <span class="text-animation--underline-thin">${title}</span>
            </a>
            ${variantMarkup}
          </div>
        </div>
      </div>`;
  };

  const renderModalContent = (cart) => {
    const items = (cart.items || []).slice(0, maxItems);
    const hiddenCount = Math.max(0, cart.item_count - maxItems);
    const moreItemsMarkup = hiddenCount > 0
      ? `<p class="cart-reminder-popup__more text-size--small text-color--opacity">${replaceCount(labels.moreItems, hiddenCount)}</p>`
      : '';
    const rewardsMarkup = getRewardsMarkup();

    return `
      <div class="element--vertically-centered">
        <div class="container--small">
          <div class="modal-content cart-reminder-popup">
            <div class="modal-heading">
              <div class="modal-heading__text remove-empty-space">
                <span class="h3 popup-title">${escapeHtml(getTitle(cart.item_count))}</span>
              </div>
              <div class="modal-heading__actions">
                <button class="modal-close" data-js-close data-js-first-focus>
                  ${window.KROWN?.settings?.symbols?.close || '&times;'}
                </button>
              </div>
            </div>

            <p class="cart-reminder-popup__meta text-size--small text-color--opacity">
              ${getMetaText(cart.item_count, cart.items_subtotal_price)}
            </p>

            <div class="cart-reminder-popup__items">
              ${items.map(renderItem).join('')}
              ${moreItemsMarkup}
            </div>

            ${rewardsMarkup}
            ${getBodyMarkup()}

            <div class="flex-buttons increased-spacing cart-reminder-popup__actions">
              <form action="${escapeHtml(labels.cartUrl)}" method="post" novalidate data-cart-reminder-checkout-form>
                <button
                  type="submit"
                  name="checkout"
                  class="button button--solid button--regular button--fullwidth"
                  data-cart-reminder-checkout
                >
                  ${escapeHtml(labels.checkout)}
                </button>
              </form>
              <button
                type="button"
                class="button button--outline button--regular button--fullwidth"
                data-cart-reminder-open-cart
              >
                ${escapeHtml(labels.viewCart)}
              </button>
            </div>
          </div>
        </div>
      </div>
      <span class="modal-background" data-js-close></span>`;
  };

  const trackEvent = (event, extra = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      cart_reminder_item_count: extra.itemCount,
      cart_reminder_subtotal: extra.subtotal,
    });
  };

  const openCart = () => {
    try {
      if (window.KROWN?.settings?.cart_action === 'popup' && document.getElementById('site-cart-popup')) {
        document.getElementById('site-cart-popup').show();
      } else if (document.getElementById('site-cart-sidebar')) {
        document.getElementById('site-cart-sidebar').show();
      } else {
        window.location.href = labels.cartUrl;
      }
    } catch {
      window.location.href = labels.cartUrl;
    }
  };

  const bindModalActions = (modalCommon, cart) => {
    const viewCartBtn = modalCommon.querySelector('[data-cart-reminder-open-cart]');
    const checkoutBtn = modalCommon.querySelector('[data-cart-reminder-checkout]');

    if (viewCartBtn) {
      viewCartBtn.addEventListener('click', () => {
        trackEvent('cart_reminder_view_cart_click', {
          itemCount: cart.item_count,
          subtotal: cart.items_subtotal_price,
        });
        if (typeof modalCommon.hide === 'function') modalCommon.hide(false);
        openCart();
      });
    }

    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => {
        trackEvent('cart_reminder_checkout_click', {
          itemCount: cart.item_count,
          subtotal: cart.items_subtotal_price,
        });
      });
    }

    modalCommon.querySelectorAll('[data-js-close]').forEach((btn) => {
      btn.addEventListener('click', () => {
        trackEvent('cart_reminder_dismiss', {
          itemCount: cart.item_count,
          subtotal: cart.items_subtotal_price,
        });
      }, { once: true });
    });
  };

  const showReminderModal = (cart) => {
    const modalHost = document.querySelector(modalContentSelector);
    const modalCommon = document.getElementById('modal-common');

    if (!modalHost || !modalCommon || typeof modalCommon.show !== 'function') return;

    modalHost.innerHTML = renderModalContent(cart);
    modalCommon.show(modalContentSelector);
    sessionStorage.setItem(sessionGuardKey, 'true');

    trackEvent('cart_reminder_impression', {
      itemCount: cart.item_count,
      subtotal: cart.items_subtotal_price,
    });

    setTimeout(() => bindModalActions(modalCommon, cart), 0);
  };

  const delayMs = Math.max(0, delaySeconds) * 1000;

  fetch(`${labels.cartUrl}.js`, { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .then((cart) => {
      if (!cart || typeof cart.item_count !== 'number') return;
      if (cart.item_count <= 0) return;
      setTimeout(() => showReminderModal(cart), delayMs);
    })
    .catch(() => {});
})();
